import React from 'react';
import { notifications, extractApiErrorMessage, colors } from '../../utils';
import { Title, Loader, ReloadIconButton } from '../../elements';

import {
  Chip,
  Icon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { LabelPrintTaskService } from '../../hooks';
import { LabelPrintTask, LabelPrintTaskStatus } from '../../models/labels';
import { AnimalType } from '../../models/animal';
import { tokens } from '../../theme/tokens';

/** The status chip's colour: red for an error, green for a success, the primary colour otherwise. */
const statusColour = (status: LabelPrintTaskStatus) =>
  status === LabelPrintTaskStatus.Error
    ? colors.red
    : status === LabelPrintTaskStatus.Success
      ? colors.green
      : colors.blue;

const LabelPrintTasks: React.FC<any> = () => {
  const labelPrintTaskService = new LabelPrintTaskService();
  const { t } = useTranslation();

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [labelPrintTasks, setLabelPrintTasks] = React.useState([] as LabelPrintTask[]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const loadLabelPrintTasks = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    labelPrintTaskService
      .getLabelPrintTasks(searchPage, rowsPerPage)
      .then((result) => {
        setLabelPrintTasks(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const load = async () => loadLabelPrintTasks();
      load();
    }
  }, [loaded]);

  const renderStatusCell = (task: LabelPrintTask) => {
    const colour = statusColour(task.status);

    return (
      <Chip
        size="small"
        variant="outlined"
        label={LabelPrintTaskStatus[task.status]}
        sx={{ color: colour, borderColor: colour }}
      />
    );
  };

  return (
    // v4's layout, kept: a spacing-2 grid (pulled 8px out, cells padded 8px) whose heading cell is itself a
    // spacing-1 row; the two header cells split the width from 960px up.
    <div className="-m-2 box-border flex w-[calc(100%+16px)] flex-wrap">
      <div className="-m-1 box-border flex w-[calc(100%+8px)] flex-wrap p-2 min-[960px]:w-1/2">
        <div className="box-border p-1">
          <Icon fontSize="large" color="primary">
            {'print'}
          </Icon>
        </div>
        <div className="box-border p-1">
          <Title>{t('f-label-task')}</Title>
        </div>
        <div className="box-border p-1">
          <Chip label={total} variant="outlined" />
        </div>
      </div>

      <div className="box-border flex w-full flex-wrap items-center justify-end p-2 min-[960px]:w-1/2">
        <div>
          <ReloadIconButton action={() => setLoaded(false)} />
        </div>
      </div>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && (
        <div className="box-border flex w-full flex-wrap p-2">
          <div className="box-border w-full">
            <Paper elevation={1}>
              <Table>
                <TableHead sx={{ background: tokens.sidebar }}>
                  <TableRow>
                    <TableCell align="center">{t('f-com-status')}</TableCell>
                    <TableCell>{t('f-supp-order-number')}</TableCell>
                    <TableCell>{t('f-com-date-time')}</TableCell>
                    <TableCell>{t('f-com-supply')}</TableCell>
                    <TableCell>{t('f-com-amount')}</TableCell>
                    <TableCell>{t('f-com-animal-type')}</TableCell>
                    <TableCell>{t('f-com-farm')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {labelPrintTasks.map((task) => (
                    <TableRow key={task.id} hover>
                      <TableCell align="center">{renderStatusCell(task)}</TableCell>
                      <TableCell>{`#${task.order.orderNumber}`}</TableCell>
                      <TableCell>{new Date(task.created).toLocaleString()}</TableCell>
                      <TableCell>{task.supply.number}</TableCell>
                      <TableCell>{`${parseInt(task.supply.size) * task.requestedCount} CC`}</TableCell>
                      <TableCell>{t(AnimalType[task.supply.animalType])}</TableCell>
                      <TableCell>{`${task.farm.number} | ${task.farm.name}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelPrintTasks;
